import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { Popper } from '@mui/base/Popper';
import { useCallback, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
	ButtonCancelInline,
	ButtonIconDeleteInline,
	ButtonSaveInline,
} from '@adflow/frontend/ui-buttons';
import {
	HubTooltip,
	LineClamp,
	notchedPopperModifier,
} from '@adflow/frontend/ui-common';
import { su, theme } from '@adflow/frontend/ui-theme';
import { createErrorToast } from '@adflow/frontend/ui-toasts';
import { useClickIgnoreDrag } from '@adflow/frontend/util-common';

import { TextareaWithControl } from '../Textarea/TextareaWithControl';

import { InputNumberFormatFieldWithControl } from './InputNumberFormatFieldWithControl';
import { InputTextFieldWithControl } from './InputTextFieldWithControl';

const styles = {
	container: css({ width: '100%' }),
	label: css(su.textXs, {
		color: theme.colors.text.secondary,
	}),
	link: css({
		color: theme.colors.text.tableHeader,
		fontStyle: 'italic',
		fontWeight: '300',
		cursor: 'pointer',
	}),
	value: css({
		color: theme.colors.text.tableHeader,
		fontWeight: '500',
		cursor: 'text',
	}),
	timeContainer: css({ display: 'flex', flexWrap: 'wrap' }),
	timeMessage: css(su.textSm, {
		color: theme.colors.text.tableHeader,
		fontWeight: '400',
	}),
	disabled: css({
		color: theme.colors.text.disabled,
		cursor: 'default',
	}),
};

interface InputTextFieldInlineProps {
	updateText: (text: string) => void;
	readOnly?: boolean;
	defaultValue: string;
	disabled?: boolean;
	label?: string;
	placeholder?: string;
	emptyPlaceholder?: string;
	textArea?: boolean;
	numberFormat?: boolean;
	decimalScale?: number;
	allowNegative?: boolean;
	testid?: string;
	labelStyle?: CSSInterpolation;
	outerStyle?: CSSInterpolation;
	textStyle?: CSSInterpolation;
	maxLength?: number;
}

export function InputTextFieldInline({
	updateText,
	readOnly,
	defaultValue,
	disabled,
	label,
	placeholder,
	emptyPlaceholder,
	textArea = false,
	numberFormat = false,
	decimalScale = 2,
	allowNegative,
	testid,
	labelStyle,
	outerStyle,
	textStyle,
	maxLength,
}: InputTextFieldInlineProps) {
	const { t } = useTranslation(['common']);

	const formMethods = useForm({
		defaultValues: {
			text: defaultValue ?? '',
		},
	});

	const [containerElement, setContainerElement] =
		useState<HTMLDivElement | null>(null);

	const text = useWatch({ control: formMethods.control, name: 'text' });

	const [showInput, setShowInput] = useState(false);
	const [savedText, setSavedText] = useState(defaultValue);

	const handleSubmit = formMethods.handleSubmit(({ text: formText }) => {
		updateText(formText);
	});

	const handleSave = async () => {
		if (showInput) setShowInput(false);

		if (!emptyPlaceholder && text.length === 0) {
			createErrorToast(t('error Cannot be empty'));
			formMethods.setValue('text', savedText);
		} else if (text !== savedText) {
			await handleSubmit();
			setSavedText(text);
		}
	};

	const onInputClickProps = useClickIgnoreDrag(
		useCallback(() => {
			if (disabled) return;
			if (!readOnly) setShowInput(true);
		}, [disabled, readOnly])
	);

	// TODO possible refactor to a controlled component (remove use of react-hook-form)
	return (
		<FormProvider {...formMethods}>
			<div css={styles.container}>
				<p css={styles.label ? labelStyle : null}>{label}</p>
				{showInput ? (
					<ClickAwayListener onClickAway={handleSave}>
						<div
							css={{
								width: '100%',
								display: showInput ? 'block' : 'none',
							}}
							ref={setContainerElement}
						>
							{textArea ? (
								<TextareaWithControl
									name="text"
									autoFocus
									helperStyle={{ minHeight: '0px' }}
									placeholder={placeholder}
									outerStyle={[{ width: '100%' }, textStyle]}
									disabled={!showInput}
									data-testid={`${testid}-inline-input-input`}
									maxLength={maxLength}
								/>
							) : numberFormat ? (
								<InputNumberFormatFieldWithControl
									name="text"
									autoFocus
									decimalScale={decimalScale}
									helperStyle={{ minHeight: '0px' }}
									placeholder={placeholder}
									outerStyle={[{ width: '100%' }, textStyle]}
									data-testid={`${testid}-inline-input-input`}
									allowNegative={allowNegative}
								/>
							) : (
								<InputTextFieldWithControl
									name="text"
									autoFocus
									helperStyle={{ minHeight: '0px' }}
									placeholder={placeholder}
									outerStyle={[{ width: '100%' }, textStyle]}
									disabled={!showInput}
									onKeyDown={async (e) => {
										if (e.key === 'Enter') {
											await handleSave();
										}
									}}
									data-testid={`${testid}-inline-input-input`}
									maxLength={maxLength}
								/>
							)}

							{showInput && containerElement ? (
								<Popper
									open
									anchorEl={containerElement}
									placement="bottom-end"
									modifiers={[
										{ name: 'offset', options: { offset: [0, -1] } },
										notchedPopperModifier(),
									]}
								>
									<div css={{ display: 'flex' }}>
										<HubTooltip title={t('Clear content')}>
											<div>
												<ButtonIconDeleteInline
													onClick={() => {
														formMethods.setValue('text', '');
													}}
													data-testid={`${testid}-inline-input-clear`}
												/>
											</div>
										</HubTooltip>
										<ButtonCancelInline
											css={{ marginLeft: '1rem' }}
											onClick={() => {
												setShowInput(false);
												formMethods.setValue('text', savedText);
											}}
											data-testid={`${testid}-inline-input-cancel`}
										/>

										<ButtonSaveInline
											css={{ marginLeft: '1rem', marginRight: '0.75rem' }}
											onClick={() => handleSave()}
											data-testid={`${testid}-inline-input-save`}
										/>
									</div>
								</Popper>
							) : null}
						</div>
					</ClickAwayListener>
				) : null}
				<HubTooltip title={savedText ?? null} placement="bottom">
					<div
						css={[
							showInput && { visibility: 'hidden', height: 0 },
							!(readOnly || disabled) && [
								su.roundedMd,
								{
									'&:hover': {
										backgroundColor:
											theme.colors.background.dropdown.hoverItemBackground,
									},
									transitionProperty:
										'background-color, border-color, color, fill, stroke',
									transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
									transitionDuration: '150ms',
									cursor: 'text',
								},
							],
							outerStyle,
							textStyle,
						]}
						{...onInputClickProps}
						data-testid={`${testid}-inline-input-button`}
					>
						{savedText ? (
							<LineClamp
								css={
									readOnly || disabled
										? {
												color: theme.colors.text.disabled,
												fontStyle: 'italic',
												fontWeight: '300',
												cursor: 'not-allowed',
											}
										: styles.value
								}
							>
								{savedText}
							</LineClamp>
						) : (
							<p
								css={
									readOnly || disabled
										? {
												color: theme.colors.text.disabled,
												fontStyle: 'italic',
												fontWeight: '300',
												cursor: 'not-allowed',
											}
										: styles.link
								}
							>
								{emptyPlaceholder}
							</p>
						)}
					</div>
				</HubTooltip>
			</div>
		</FormProvider>
	);
}
